import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  ArrowBack,
  Add,
  Download,
  Edit,
  Print,
  SwapHoriz,
  Warning,
  Info,
  Person,
  Class as ClassIcon,
  Schedule,
  MenuBook,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import Menu from "@mui/material/Menu";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useEffectiveRolePath, { normalizeRoleSlug } from "../../../hooks/useEffectiveRolePath";
import { useTimetableById, useRequestScheduleChange, useExportTimetable } from "../../../hooks/queries/useTimetable";
import { useClasses } from "../../../hooks/queries/useCourses";
import { toast } from "react-toastify";
import {
  buildViewTimeSlotOptions,
  buildViewSlotsFromSchedule,
  toTimeInputValue,
  slotOverlapsScheduleCell,
  slotStartsInScheduleCell,
  isSubjectTimetableCellBlocked,
  isTeacherTimetableCellBlocked,
} from "../../../utils/timetableTimeSlots";
import { getScheduleSlotsPerDayFromDetail } from "../../../utils/timetableScheduleFromDetail";
import SwapBadge from "../../../components/common/SwapBadge";
import ExtendBadge from "../../../components/common/ExtendBadge";

const DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
const FALLBACK_TIME_SLOTS = buildViewTimeSlotOptions();

const getSubjectLabel = (slot) => {
  const subj = slot?.subject || {};
  const name = subj.name || subj.subjectName || "";
  const code = subj.code || "";
  if (name && code) return `${name} (${code})`;
  return name || code || "Unassigned";
};

const getClassLabel = (slot) => {
  const className = slot?.class?.name || "";
  const classSection = slot?.class?.section || "";
  if (!className) return "Unassigned";
  return classSection ? `${className} - ${classSection}` : className;
};

const viewModeMeta = (mode) => {
  if (mode === "teacher") {
    return { selectLabel: "Select Teacher", editLabel: "Teacher" };
  }
  if (mode === "subject") {
    return { selectLabel: "Select Subject", editLabel: "Subject" };
  }
  return { selectLabel: "Select Class", editLabel: "Class" };
};

// Natural, case-insensitive comparator so grouped keys like "FE - A", "FE - B", "FE - F"
// sort A→B→…→F (and "SE - 10" after "SE - 2"), instead of plain string order which put
// "FE - F" right after "FE - A". `numeric` handles numeric sections; sensitivity:"base"
// makes it case-insensitive. Falls back to a stable string compare on bad input.
const naturalGroupCompare = (a, b) => {
  const sa = String(a ?? "");
  const sb = String(b ?? "");
  try {
    return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: "base" });
  } catch {
    return sa < sb ? -1 : sa > sb ? 1 : 0;
  }
};

const TimetableView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { rolePath } = useEffectiveRolePath();
  const user = useSelector((state) => state.auth?.user);
  const authRoleLower = normalizeRoleSlug(user?.role?.slug || user?.role || "");
  const canApproveScheduleChange = ["admin", "superadmin", "institute", "hod"].includes(authRoleLower);
  /**
   * Only teaching staff (teacher/HOD) own lecture slots and may request schedule changes.
   * Institute Admin (normalizeRoleSlug maps "institute" -> "admin") and Super Admin only
   * review/approve/reject requests — they must never see the "request change" action.
   */
  const canOpenSlotScheduleChangeDialog = ["teacher", "hod"].includes(authRoleLower);
  const [changeRequestOpen, setChangeRequestOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [changeReason, setChangeReason] = useState("");
  const [changeType, setChangeType] = useState("time_change");
  const [newDay, setNewDay] = useState("");
  const [newStartTime, setNewStartTime] = useState("");
  const [newEndTime, setNewEndTime] = useState("");
  const [preferredDate, setPreferredDate] = useState(null);
  const [priority, setPriority] = useState("medium");
  const [requestError, setRequestError] = useState("");
  const [viewMode, setViewMode] = useState("class"); // 'class' | 'teacher' | 'subject'
  const [selectedFilter, setSelectedFilter] = useState("");
  const [exportAnchor, setExportAnchor] = useState(null);
  const getTeacherName = (t) => t?.User?.fullName || t?.user?.fullName || [t?.user?.firstName, t?.user?.lastName].filter(Boolean).join(" ").trim() || [t?.User?.firstName, t?.User?.lastName].filter(Boolean).join(" ").trim() || t?.name || t?.teacherName || "";

  const { data: timetableData, isLoading, error } = useTimetableById(id);
  const requestChangeMutation = useRequestScheduleChange();
  const exportMutation = useExportTimetable();

  const detail = timetableData;
  const slotsList = React.useMemo(() => (Array.isArray(detail?.slots) ? detail.slots : []), [detail?.slots]);

  // Classes belonging to this timetable's institute + department, so we can offer to start a
  // timetable for a class that has no slots yet. departmentId null = institute-level bucket.
  const timetableInstituteId = detail?.instituteId ?? null;
  const timetableDepartmentId = detail?.departmentId ?? null;
  const { data: deptClassesData, isLoading: deptClassesLoading } = useClasses(
    {
      page: 1,
      limit: "all",
      sort: "name",
      order: "ASC",
      ...(timetableInstituteId && { instituteId: timetableInstituteId }),
      ...(timetableDepartmentId != null && { departmentId: timetableDepartmentId }),
    },
    { enabled: !!timetableInstituteId }
  );
  const departmentClasses = React.useMemo(() => {
    const raw = Array.isArray(deptClassesData?.data)
      ? deptClassesData.data
      : deptClassesData?.data?.classes || deptClassesData?.data?.data || [];
    return Array.isArray(raw) ? raw : [];
  }, [deptClassesData]);

  const scheduleSlotsPerDay = React.useMemo(
    () => getScheduleSlotsPerDayFromDetail(detail),
    [detail]
  );

  const stats = React.useMemo(() => {
    const uniqueClasses = new Set(slotsList.map((s) => s.classId).filter((id) => id != null));
    const uniqueTeachers = new Set(slotsList.map((s) => s.teacherId).filter((id) => id != null));
    const uniqueSubjects = new Set(
      slotsList.map((s) => s.subjectId ?? s.subject?.id).filter((id) => id != null)
    );
    return {
      totalSlots: slotsList.length,
      classesCount: uniqueClasses.size,
      teachersCount: uniqueTeachers.size,
      subjectsCount: uniqueSubjects.size,
    };
  }, [slotsList]);

  /** Saved school day + breaks from generation; otherwise full-day hourly fallback. */
  const viewTimeSlots = React.useMemo(() => {
    const fromSchedule = buildViewSlotsFromSchedule(scheduleSlotsPerDay);
    if (fromSchedule) return fromSchedule;
    return FALLBACK_TIME_SLOTS.map((t) => ({ ...t, slotType: "lecture" }));
  }, [scheduleSlotsPerDay]);

  const buildCalendarGrid = React.useCallback(
    (slots) => {
      const grid = {};
      const allTimeSlots = new Set(viewTimeSlots.map(ts => ts.value));
      
      // Add time slots from actual slots
      slots.forEach((slot) => {
        if (slot.timeSlot) {
          allTimeSlots.add(slot.timeSlot);
        }
      });
      
      const sortedTimeSlots = Array.from(allTimeSlots).sort();
      
      DAYS.forEach((day) => {
        grid[day] = {};
        sortedTimeSlots.forEach((timeSlotValue) => {
          // VIEW mode must support parallel slots at same time (array like EDIT mode).
          grid[day][timeSlotValue] = [];
        });
      });

      slots.forEach((slot) => {
        const day = slot.day;
        const timeKey = slot.timeSlot;
        if (grid[day] && grid[day][timeKey] !== undefined) {
          grid[day][timeKey].push(slot);
        }
      });

      return { grid, timeSlots: sortedTimeSlots };
    },
    [viewTimeSlots]
  );

  const handleRequestChange = (slot) => {
    if (!canOpenSlotScheduleChangeDialog) return;
    setSelectedSlot(slot);
    setNewDay(slot.dayOfWeek || "");
    setNewStartTime(toTimeInputValue(slot.startTime));
    setNewEndTime(toTimeInputValue(slot.endTime));
    setRequestError("");
    setChangeRequestOpen(true);
  };

  const handleSubmitChangeRequest = async () => {
    try {
      if (!selectedSlot?.id) {
        toast.error("No slot selected");
        return;
      }
      if (!changeReason.trim()) {
        setRequestError("Reason is required");
        return;
      }
      if (changeType === "time_change") {
        if (!newStartTime || !newEndTime) {
          setRequestError("New start time and end time are required");
          return;
        }
        if (newEndTime <= newStartTime) {
          setRequestError("New end time must be after new start time");
          return;
        }
      }

      const preferredDateValue = preferredDate
        ? new Date(preferredDate).toISOString().slice(0, 10)
        : null;
      const derivedNewDay = preferredDateValue
        ? new Date(preferredDateValue).toLocaleDateString("en-US", { weekday: "long" }).toUpperCase()
        : changeType === "date_change" ? newDay : null;

      await requestChangeMutation.mutateAsync({
        timeTableSlotId: selectedSlot.id,
        changeType,
        newDay: derivedNewDay,
        newStartTime: changeType === "time_change" ? newStartTime : null,
        newEndTime: changeType === "time_change" ? newEndTime : null,
        preferredDate: preferredDateValue,
        reason: changeReason,
        priority,
      });

      toast.success("Schedule change request submitted successfully");
      setChangeRequestOpen(false);
      setChangeReason("");
      setChangeType("time_change");
      setPriority("medium");
      setPreferredDate(null);
      setSelectedSlot(null);
      setRequestError("");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to submit schedule change request";
      setRequestError(message);
      toast.error(message);
    }
  };

  const handleExport = async (format = "pdf") => {
    try {
      const res = await exportMutation.mutateAsync({ id, format });
      const blob = res?.data ?? res;
      if (blob && (blob instanceof Blob || blob instanceof ArrayBuffer)) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `timetable_${id}.${format === "excel" || format === "xlsx" ? "xlsx" : format}`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("Export downloaded");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Export failed");
    }
  };

  const handlePrint = () => {
    // Use browser print dialog; users can "Save as PDF".
    window.print();
  };

  // Group slots by class or teacher
  const groupedSlots = React.useMemo(() => {
    if (!slotsList.length) return {};

    console.log('Grouping slots:', slotsList.length);
    const groups = {};
    
    // Helper function to convert time to minutes
    const timeToMinutes = (hhmm) => {
      if (!hhmm || typeof hhmm !== "string") return 0;
      const s = hhmm.trim().slice(0, 5);
      const [h, m] = s.split(":").map((x) => Number.parseInt(x, 10));
      if (Number.isNaN(h) || Number.isNaN(m)) return 0;
      return h * 60 + m;
    };
    
    // Helper function to find matching time slot
    const findMatchingTimeSlot = (slot, startTime) => {
      const slotStartMinutes = timeToMinutes(startTime);
      for (const timeSlot of viewTimeSlots) {
        const [slotStart, slotEnd] = timeSlot.value.split('-');
        const slotStartMin = timeToMinutes(slotStart);
        const slotEndMin = timeToMinutes(slotEnd);
        if (slotStartMinutes >= slotStartMin && slotStartMinutes < slotEndMin) {
          return timeSlot.value;
        }
      }
      // If no match found, create a time slot for this specific time
      const startFormatted = toTimeInputValue(startTime);
      const endFormatted = toTimeInputValue(slot.endTime || startTime);
      return `${startFormatted}-${endFormatted}`;
    };
    
    slotsList.forEach((slot) => {
      console.log('Slot:', slot.dayOfWeek, slot.startTime, slot.endTime, 'Class:', slot.class?.name, 'Teacher:', slot.teacher?.User?.fullName);
      let key = "Unassigned";
      if (viewMode === "class") {
        key = getClassLabel(slot);
      } else if (viewMode === "teacher") {
        key = getTeacherName(slot.teacher) || "Unassigned";
      } else if (viewMode === "subject") {
        key = getSubjectLabel(slot);
      }
      
      console.log('Key for slot:', key);
      if (!groups[key]) {
        groups[key] = [];
      }
      
      const matchingTimeSlot = findMatchingTimeSlot(slot, slot.startTime);
      const transformedSlot = {
        ...slot,
        day: slot.dayOfWeek,
        timeSlot: matchingTimeSlot,
      };
      console.log('Transformed slot:', transformedSlot.day, transformedSlot.timeSlot);
      groups[key].push(transformedSlot);
    });
    
    console.log('Grouped slots:', groups);
    return groups;
  }, [slotsList, viewMode, viewTimeSlots]);

  const filteredGroups = React.useMemo(() => {
    if (!selectedFilter) return groupedSlots;
    return { [selectedFilter]: groupedSlots[selectedFilter] || [] };
  }, [groupedSlots, selectedFilter]);

  // Classes in this department that have NO slots in this timetable yet — offered as
  // "Add Class Timetable" so staff can start building their schedule. Only relevant in the
  // By-Class view. A class is "covered" when at least one slot references its id.
  const classesWithoutTimetable = React.useMemo(() => {
    if (viewMode !== "class") return [];
    const coveredClassIds = new Set(
      slotsList.map((s) => s.classId ?? s.class?.id).filter((cid) => cid != null).map(Number)
    );
    return departmentClasses
      .filter((c) => {
        const cid = c?.id ?? c?.classId;
        return cid != null && !coveredClassIds.has(Number(cid));
      })
      .map((c) => {
        const name = c?.name || c?.className || "";
        const section = c?.section || c?.classSection || "";
        return {
          id: c?.id ?? c?.classId,
          label: name ? (section ? `${name} - ${section}` : name) : `Class ${c?.id ?? c?.classId}`,
        };
      })
      .sort((a, b) => naturalGroupCompare(a.label, b.label));
  }, [viewMode, slotsList, departmentClasses]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        gap={2}
      >
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary">
          Loading timetable data...
        </Typography>
      </Box>
    );
  }

  if (error || !detail) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load timetable: {error?.message || "Timetable not found"}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          "@media print": { display: "none" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={() => navigate(`${rolePath}/timetable`)}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Timetable Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {detail?.academicYear ?? "—"}
              {detail?.semester != null && detail?.semester !== "" ? ` - ${detail.semester}` : ""}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
          >
            Export
          </Button>
          <Button variant="outlined" startIcon={<Print />} onClick={handlePrint}>
            Print
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={!!exportAnchor}
            onClose={() => setExportAnchor(null)}
          >
            <MenuItem onClick={() => { handleExport("pdf"); setExportAnchor(null); }}>
              Export as PDF
            </MenuItem>
            <MenuItem onClick={() => { handleExport("excel"); setExportAnchor(null); }}>
              Export as Excel
            </MenuItem>
            <MenuItem onClick={() => { handleExport("csv"); setExportAnchor(null); }}>
              Export as CSV
            </MenuItem>
          </Menu>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate(`${rolePath}/timetable/edit/${id}`)}
          >
            Edit
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3, "@media print": { display: "none" } }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Schedule color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Total Slots
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={600}>
                {stats.totalSlots}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <ClassIcon color="info" />
                <Typography variant="body2" color="text.secondary">
                  Classes
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={600}>
                {stats.classesCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <MenuBook color="success" />
                <Typography variant="body2" color="text.secondary">
                  Subjects
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={600}>
                {stats.subjectsCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Person color="secondary" />
                <Typography variant="body2" color="text.secondary">
                  Teachers
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={600}>
                {stats.teachersCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* View Mode and Filter Controls */}
      <Card sx={{ mb: 3, "@media print": { display: "none" } }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>View By</InputLabel>
                <Select
                  value={viewMode}
                  label="View By"
                  onChange={(e) => {
                    setViewMode(e.target.value);
                    setSelectedFilter("");
                  }}
                >
                  <MenuItem value="class">By Class</MenuItem>
                  <MenuItem value="teacher">By Teacher</MenuItem>
                  <MenuItem value="subject">By Subject</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <FormControl fullWidth>
                <InputLabel>{viewModeMeta(viewMode).selectLabel}</InputLabel>
                <Select
                  value={selectedFilter}
                  label={viewModeMeta(viewMode).selectLabel}
                  onChange={(e) => setSelectedFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {Object.keys(groupedSlots)
                    .sort(naturalGroupCompare)
                    .map((key) => (
                      <MenuItem key={key} value={key}>
                        {key}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Classes in this department without a timetable yet — start one directly. */}
      {viewMode === "class" && !selectedFilter && classesWithoutTimetable.length > 0 && (
        <Card sx={{ mb: 3, "@media print": { display: "none" } }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <ClassIcon color="action" />
              <Typography variant="h6">Classes without a timetable</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These classes in this department have no lectures scheduled yet. Start building a
              timetable for a class below.
            </Typography>
            {deptClassesLoading ? (
              <CircularProgress size={22} />
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {classesWithoutTimetable.map((c) => (
                  <Button
                    key={c.id}
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={() =>
                      navigate(
                        `${rolePath}/timetable/edit/${id}?filter=${encodeURIComponent(
                          c.id
                        )}&mode=class`
                      )
                    }
                  >
                    Add {c.label} Timetable
                  </Button>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Calendar Grid for Each Group (natural order: FE - A, FE - B, …) */}
      {Object.entries(filteredGroups)
        .sort(([a], [b]) => naturalGroupCompare(a, b))
        .map(([groupName, slots]) => {
        console.log('Building grid for', groupName, 'with slots:', slots);
        const { grid, timeSlots } = buildCalendarGrid(slots);
        console.log('Grid for', groupName, grid);
        
        return (
          <Card key={groupName} sx={{ mb: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  "@media print": { display: "none" },
                }}
              >
                <Typography variant="h6">
                  {groupName}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Edit />}
                  onClick={() => navigate(`${rolePath}/timetable/edit/${id}?filter=${encodeURIComponent(groupName)}&mode=${viewMode}`)}
                >
                  Edit {viewModeMeta(viewMode).editLabel} Timetable
                </Button>
              </Box>
              
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  "@media print": {
                    overflow: "visible",
                    borderRadius: 0,
                  },
                }}
              >
                <Table
                  size="small"
                  sx={{
                    "@media print": {
                      tableLayout: "fixed",
                      width: "100%",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: "primary.main" }}>
                      <TableCell sx={{ bgcolor: "primary.main", fontWeight: 600, minWidth: 120, color: "white" }}>
                        Time
                      </TableCell>
                      {DAYS.map((day) => (
                        <TableCell
                          key={day}
                          align="center"
                          sx={{ bgcolor: "primary.main", fontWeight: 600, color: "white" }}
                        >
                          {day}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timeSlots.map((timeSlotValue) => {
                      // Find the corresponding time slot info
                      const timeSlotInfo = viewTimeSlots.find(ts => ts.value === timeSlotValue) || 
                        { label: timeSlotValue.replace('-', ' – '), slotType: 'lecture' };
                      return (
                        <TableRow
                          key={timeSlotValue}
                          sx={{
                            "@media print": {
                              breakInside: "avoid",
                              pageBreakInside: "avoid",
                            },
                          }}
                        >
                          <TableCell sx={{ bgcolor: "grey.50", fontWeight: 500, border: "1px solid", borderColor: "divider" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                              <span>{timeSlotInfo.label}</span>
                              {timeSlotInfo.slotType === "break" && (
                                <Chip label="Break" size="small" color="warning" variant="outlined" sx={{ height: 22 }} />
                              )}
                            </Box>
                          </TableCell>
                          {DAYS.map((day) => {
                            const slotsAt = Array.isArray(grid?.[day]?.[timeSlotValue]) ? grid[day][timeSlotValue] : [];
                            const isBreak = timeSlotInfo.slotType === "break";
                            const [cellStart, cellEnd] = String(timeSlotValue).split("-");
                            const dayUpper = day.toUpperCase();
                            // Check if a multi-hour slot from this group spans into this cell
                            const isCoveredBySpanningSlot =
                              !isBreak &&
                              slotsAt.length === 0 &&
                              slots.some((s) => {
                                if (String(s.dayOfWeek || s.day || "").toUpperCase() !== dayUpper) return false;
                                if (slotStartsInScheduleCell(s, cellStart, cellEnd)) return false;
                                return slotOverlapsScheduleCell(s, cellStart, cellEnd);
                              });
                            const isCellBlocked =
                              !isBreak &&
                              slotsAt.length === 0 &&
                              !isCoveredBySpanningSlot &&
                              (viewMode === "subject"
                                ? isSubjectTimetableCellBlocked({
                                    allSlots: slotsList,
                                    day,
                                    cellStart,
                                    cellEnd,
                                    subjectFilter: groupName,
                                    subjectGroupSlots: slots,
                                    slotMatchesSubjectFilter: (s, f) => getSubjectLabel(s) === f,
                                  })
                                : viewMode === "teacher"
                                  ? isTeacherTimetableCellBlocked({
                                      allSlots: slotsList,
                                      day,
                                      cellStart,
                                      cellEnd,
                                      teacherFilter: groupName,
                                      teacherGroupSlots: slots,
                                      getTeacherName,
                                    })
                                  : false);
                            let bgcolor = "grey.200";
                            // Avoid success green background in VIEW mode.
                            if (slotsAt.length > 0) bgcolor = "grey.100";
                            else if (isBreak) bgcolor = "warning.light";
                            else if (isCoveredBySpanningSlot) bgcolor = "grey.100";
                            return (
                              <TableCell
                                key={`${day}-${timeSlotValue}`}
                                align="center"
                                sx={{
                                  p: 1,
                                  minHeight: 80,
                                  border: "1px solid",
                                  borderColor: "divider",
                                  bgcolor,
                                  cursor: !isBreak && slotsAt.length > 0 ? "default" : "default",
                                  "&:hover":
                                    !slotsAt.length && !isBreak
                                      ? { bgcolor: "grey.300" }
                                      : {},
                                  transition: "all 0.2s ease",
                                  "@media print": {
                                    breakInside: "avoid",
                                    pageBreakInside: "avoid",
                                    overflow: "hidden",
                                    maxWidth: 0,
                                    verticalAlign: "top",
                                  },
                                }}
                              >
                                {isBreak ? (
                                  <Typography variant="body2" fontWeight={600} color="warning.dark">
                                    {timeSlotInfo.breakLabel || "Break"}
                                  </Typography>
                                ) : slotsAt.length > 0 ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.75,
                                      width: "100%",
                                      "@media print": {
                                        breakInside: "avoid",
                                        pageBreakInside: "avoid",
                                        maxWidth: "100%",
                                        overflow: "hidden",
                                      },
                                    }}
                                  >
                                    {slotsAt.map((slot) => {
                                      const canRequestChange =
                                        Boolean(slot && slot.id) && canOpenSlotScheduleChangeDialog;
                                      const lectureType =
                                        String(slot.lectureType || slot.type || (slot.practicalBatchCode ? "PRACTICAL" : "LECTURE"))
                                          .toUpperCase();
                                      return (
                                        <Box
                                          key={slot.id ?? `${day}-${timeSlotValue}-${toTimeInputValue(slot.startTime)}-${toTimeInputValue(slot.endTime)}`}
                                          role={canRequestChange ? "button" : undefined}
                                          tabIndex={canRequestChange ? 0 : undefined}
                                          onClick={() => canRequestChange && handleRequestChange(slot)}
                                          onKeyDown={(e) => {
                                            if (!canRequestChange) return;
                                            if (e.key === "Enter" || e.key === " ") {
                                              e.preventDefault();
                                              handleRequestChange(slot);
                                            }
                                          }}
                                          sx={{
                                            textAlign: "center",
                                            borderRadius: 1,
                                            px: 1,
                                            py: 0.75,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            bgcolor: "background.paper",
                                            color: "text.primary",
                                            cursor: canRequestChange ? "pointer" : "default",
                                            ...(canRequestChange
                                              ? {
                                                  "&:hover": { bgcolor: "action.hover" },
                                                }
                                              : {}),
                                            transition: "background-color 0.2s ease",
                                            "@media print": {
                                              breakInside: "avoid",
                                              pageBreakInside: "avoid",
                                              maxWidth: "100%",
                                              overflow: "hidden",
                                            },
                                          }}
                                        >
                                          <Typography variant="caption" display="block" sx={{ mb: 0.25, opacity: 0.9 }}>
                                            {toTimeInputValue(slot.startTime)} - {toTimeInputValue(slot.endTime)}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            fontWeight={700}
                                            display="block"
                                            title={
                                              viewMode === "subject"
                                                ? getClassLabel(slot)
                                                : slot.subject?.name || "Subject"
                                            }
                                            sx={{
                                              mb: 0.25,
                                              "@media print": {
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                                overflowWrap: "anywhere",
                                                lineHeight: 1.2,
                                                fontSize: "0.65rem",
                                              },
                                            }}
                                          >
                                            {viewMode === "subject"
                                              ? getClassLabel(slot)
                                              : slot.subject?.name || "Subject"}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                              mb: 0.5,
                                              opacity: 0.9,
                                              "@media print": {
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                                overflowWrap: "anywhere",
                                                fontSize: "0.6rem",
                                                lineHeight: 1.2,
                                              },
                                            }}
                                          >
                                            {viewMode === "class"
                                              ? getTeacherName(slot.teacher) || "Teacher"
                                              : viewMode === "teacher"
                                                ? getClassLabel(slot)
                                                : getTeacherName(slot.teacher) || "Teacher"}
                                          </Typography>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              gap: 0.5,
                                              justifyContent: "center",
                                              flexWrap: "wrap",
                                              "@media print": { display: "none" },
                                            }}
                                          >
                                            <Chip
                                              label={lectureType}
                                              size="small"
                                              variant="outlined"
                                              sx={{ height: 20, fontSize: "0.7rem" }}
                                            />
                                            {slot.practicalBatchCode ? (
                                              <Chip
                                                label={`Batch ${String(slot.practicalBatchCode).toUpperCase()}`}
                                                size="small"
                                                color="secondary"
                                                variant="outlined"
                                                sx={{ height: 20, fontSize: "0.7rem" }}
                                              />
                                            ) : null}
                                            {slot.labNo ? (
                                              <Chip
                                                label={`Lab ${slot.labNo}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ height: 20, fontSize: "0.7rem" }}
                                              />
                                            ) : null}
                                            {slot.room ? (
                                              <Chip
                                                label={`Room ${slot.room}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ height: 20, fontSize: "0.7rem" }}
                                              />
                                            ) : null}
                                            <SwapBadge
                                              isSwapped={slot.isSwapped}
                                              swapRequestId={slot.swapRequestId}
                                              swapMode={slot.swapMode}
                                              additionalSlot={slot.additionalSlot}
                                              originalTeacher={slot.originalTeacher || ""}
                                              newTeacher={
                                                slot.isSwapped
                                                  ? (viewMode === "class"
                                                      ? getTeacherName(slot.teacher)
                                                      : slot.teacherName || getTeacherName(slot.teacher))
                                                  : ""
                                              }
                                            />
                                            <ExtendBadge
                                              isExtended={slot.isExtended}
                                              isCancelledByExtend={slot.isCancelledByExtend}
                                              extendRequestId={slot.extendRequestId}
                                              extendedEndTime={slot.isExtended ? slot.endTime : null}
                                              cancelledByTeacher={slot.cancelledByTeacher}
                                              cancelledBySlot={slot.cancelledBySlot}
                                            />
                                          </Box>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                ) : isCellBlocked ? (
                                  <Typography variant="body2" sx={{ fontStyle: "italic", opacity: 0.85, color: "error.main" }}>
                                    Unavailable
                                  </Typography>
                                ) : isCoveredBySpanningSlot ? (
                                  // Continuation of a multi-period lecture that started in an
                                  // earlier row (e.g. a 09:00–11:00 slot occupies the 10:00–11:00
                                  // cell). Show it as an occupied continuation, not an empty "—".
                                  <Typography
                                    variant="caption"
                                    sx={{ fontStyle: "italic", opacity: 0.6, color: "text.secondary" }}
                                  >
                                    ↑ continued
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" sx={{ fontStyle: "italic", opacity: 0.7 }}>
                                    Available
                                  </Typography>
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        );
      })}

      {/* Change Request Dialog */}
      <Dialog
        open={changeRequestOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setChangeRequestOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SwapHoriz />
            Request Schedule Change
          </Box>
        </DialogTitle>
        <DialogContent>
          {requestError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {requestError}
            </Alert>
          )}
          {selectedSlot && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                {canApproveScheduleChange
                  ? "This creates a pending schedule change request. You can approve or reject it from the schedule change requests list."
                  : "Request a change for this time slot. An administrator will review your request."}
              </Alert>
              
              <Box sx={{ mb: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Current Day:</strong> {selectedSlot.dayOfWeek}
                </Typography>
                <Typography variant="body2">
                  <strong>Current Time:</strong> {selectedSlot.startTime} - {selectedSlot.endTime}
                </Typography>
                <Typography variant="body2">
                  <strong>Subject:</strong> {selectedSlot.subject?.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Teacher:</strong> {getTeacherName(selectedSlot.teacher) || "Teacher"}
                </Typography>
                <Typography variant="body2">
                  <strong>Class:</strong> {selectedSlot.class?.name}{selectedSlot.class?.section ? ` (${selectedSlot.class.section})` : ""}
                </Typography>
              </Box>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Change Type</InputLabel>
                <Select
                  value={changeType}
                  onChange={(e) => setChangeType(e.target.value)}
                  label="Change Type"
                >
                  <MenuItem value="time_change">Change Time</MenuItem>
                  <MenuItem value="date_change">Change Day</MenuItem>
                  <MenuItem value="room_change">Change Room</MenuItem>
                  <MenuItem value="teacher_swap">Teacher Swap</MenuItem>
                  <MenuItem value="cancel">Cancel Class</MenuItem>
                </Select>
              </FormControl>

              {changeType === "date_change" && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>New Day</InputLabel>
                  <Select
                    value={newDay}
                    onChange={(e) => setNewDay(e.target.value)}
                    label="New Day"
                  >
                    {DAYS.map((day) => (
                      <MenuItem key={day} value={day}>
                        {day}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {changeType === "time_change" && (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="New Start Time"
                        type="time"
                        value={newStartTime}
                        onChange={(e) => setNewStartTime(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="New End Time"
                        type="time"
                        value={newEndTime}
                        onChange={(e) => setNewEndTime(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
              <DatePicker
                label="Preferred Date (Optional)"
                value={preferredDate}
                onChange={(value) => setPreferredDate(value)}
                slotProps={{ textField: { fullWidth: true, sx: { mb: 2 } } }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Reason for Change Request"
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                placeholder="Please describe why this change is needed..."
                required
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setChangeRequestOpen(false)}
            disabled={requestChangeMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitChangeRequest}
            disabled={!changeReason.trim() || requestChangeMutation.isLoading}
          >
            {requestChangeMutation.isLoading ? "Submitting..." : "Submit Request"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimetableView;
